import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Downshift from 'downshift'
import PropTypes from 'prop-types'
import { addFloorAreaFilter, removeFloorAreaFilter, fetchSearchResultsIfNotBusy } from '../actions/search'
import { empty } from '../../search-common/helpers'
import { colors, fontFamilies } from '../../search-common/settings'
import ArrowThinDown from '../icons/ArrowThinDown'
import CheckMark from '../icons/CheckMark'
import { translate } from '../../modules/helper-functions'
import { media } from '../../search-common/helpers'

const Container = styled.div`
  width: 100%;
  position: relative;
  z-index: 6;
  ${media.tablet`
    max-width: 287px;
  `}
`

const Menu = styled.ul`
  max-height: 200px;
  overflow-y: auto;
  position: absolute;
  width: 100%;
  border: 1px solid ${colors.grayTone2};
  z-index: 10;
`

const Item = styled.li`
  cursor: pointer;
  font-family: ${fontFamilies.aeonik};
  font-size: 14px;
  line-height: 1;
  padding: 12px;
  min-height: 40px;
  font-weight: bold;
  background-color: ${( props ) => props.isSelected || props.isActive ? colors.grayTone1 : colors.black};
  color: ${( props ) => props.isSelected || props.isActive ? colors.highlight : colors.white};

  svg {
    float: right;
    height: 14px;
    width: 14px;
  }
`

const ToggleButton = styled.button`
  width: 100%;
  line-height: 34px;
  font-size: 14px;
  ${media.tablet`
    line-height: 40px;
    font-size: 16px;
  `}
  padding: 0 10px;
  background-color: transparent;
  color: ${colors.white};
  border: none;
  text-align: left;
  cursor: pointer;

  svg {
    float: right;
    height: calc( 1rem + 14px );
    width: calc( 1rem + 4px );
    padding: 12px 2px 2px;
    path {
      fill: ${colors.white};
    }
  }
`

const InputWrap = styled.div`
  display: flex;
  border: 1px solid ${colors.grayTone3};
`

const FloatRight = styled.span`
  float: right;
`

const Label = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 14px;
`

/**
 * PeopleAmountFilter component.
 */
class PeopleAmountFilter extends Component {
  /**
   * @constructor
   * @param {any} props
   */
  constructor( props ) {
    super( props )

    this.handleChange = this.handleChange.bind( this )
    this.itemToString = this.itemToString.bind( this )
    this.itemToMenuString = this.itemToMenuString.bind( this )
  }

  /**
   * Function handles the component state change
   * by either adding or removing the people amount
   * filter.
   *
   * @param {object} option
   * @return {object} Option.
   */
  handleChange( option ) {
    const { addFilter, removeFilter } = this.props
    if ( option ) {
      addFilter({
        min: option.min,
        max: option.max
      })
    } else {
      removeFilter()
    }
    return option
  }

  /**
   * Function returns a string representation
   * of the item.
   *
   * @param {string} item
   * @return {string}
   */
  itemToString( item ) {
    return item
      ? `${item.people}`
      : ''
  }

  /**
   * Function returns a string representation
   * of the item.
   *
   * @param {string} item
   * @return {string}
   */
  itemToMenuString( item ) {
    return item
      ? ( <span>{item.people} <FloatRight>({item.min} – {item.max}<sup>m²</sup>)</FloatRight></span> )
      : ''
  }

  /**
   * Render method.
   */
  render() {
    const { selectedItem, numberOfPeople, initialAreas, translations } = this.props
    if ( empty( numberOfPeople ) || empty( initialAreas )) {
      return null
    }
    const selectedArea = !selectedItem ? null : numberOfPeople.filter(( area ) => area.min === selectedItem.min && area.max === selectedItem.max )[0]
    return (
      <Downshift
        itemToString={this.itemToString}
        onChange={this.handleChange}
        selectedItem={selectedArea}>
        {({
          getRootProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          isOpen,
          highlightedIndex,
          getToggleButtonProps,
        }) => (
            <Container {...getRootProps()}>
              <Label {...getLabelProps()}>{translate( translations, 'Number of People' )}</Label>
              <InputWrap>
                <ToggleButton {...getToggleButtonProps()}>
                  {selectedArea ? this.itemToString( selectedArea ) : translate( translations, 'Select...' )}
                  <ArrowThinDown fill={colors.grayTone2} />
                </ToggleButton>
              </InputWrap>
              {isOpen ?
                <Menu {...getMenuProps()}>
                  {numberOfPeople.map(( type, index ) => (
                    <Item
                      key={type.people}
                      {...getItemProps({
                        item: type,
                        isSelected: selectedArea === type,
                        isActive: highlightedIndex === index
                      })}
                    >
                      {this.itemToMenuString( type )}
                      {selectedArea && selectedArea === type ? <CheckMark fill={colors.highlight} /> : null}
                    </Item>
                  ))}
                </Menu>
                : null
              }
            </Container>
          )}
      </Downshift>
    )
  }
}

PeopleAmountFilter.propTypes = {
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  selectedItem: PropTypes.object,
  numberOfPeople: PropTypes.array.isRequired,
  initialAreas: PropTypes.array.isRequired,
  translations: PropTypes.object.isRequired
}

/**
 * Map the redux state to component props.
 *
 * @param {object} state
 * @return {object}
 */
const mapStateToProps = ( state ) => ({
  selectedItem: state.search.filters.floor_area,
  numberOfPeople: state.search.initial.facets.number_of_people,
  initialAreas: state.search.all_facets.floor_areas,
  translations: state.translations
})

/**
 * Add dispatch functions to components props.
 *
 * @param {function} dispatch
 * @return {object}
 */
const mapDispatchToProps = ( dispatch ) => {
  return {
    addFilter: ( filter ) => {
      dispatch( addFloorAreaFilter( filter ))
      dispatch( fetchSearchResultsIfNotBusy())
    },
    removeFilter: () => {
      dispatch( removeFloorAreaFilter())
      dispatch( fetchSearchResultsIfNotBusy())
    }
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)( PeopleAmountFilter )
